import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { formatCurrency, formatCurrencyCompact, formatPercent } from '../../lib/format.js';
import { cn } from '@tailoring-crm/ui';
import { TONE_CLASSES } from './visuals.js';

/**
 * The two small pieces every Home sales panel writes its figures with.
 *
 * They exist so that the big-number cards, the chart tables and the month-by-month table say a
 * sum and a change in exactly the same way — the same rounding, the same colours, the same words
 * read to a screen reader. A change that was green in one panel and plain in the next, or a sum
 * that was ₹1.8L here and ₹1.82L there, would make the owner wonder which one is right.
 */

/**
 * A rupee sum written short (`₹1.8L`) for the eye, with the exact figure (`₹1,82,450.00`) kept
 * for the pointer as a `title` and for a screen reader as hidden text — so the rounding is a
 * convenience for the person glancing, never information taken away from anybody.
 */
export function CompactMoney({
  paise,
  className,
}: {
  paise: number;
  className?: string;
}): ReactElement {
  const exact = formatCurrency(paise);
  return (
    <span title={exact} className={className}>
      <span aria-hidden="true">{formatCurrencyCompact(paise)}</span>
      <span className="sr-only">{exact}</span>
    </span>
  );
}

/**
 * A change, as an arrow and a percentage: green up, red down, muted when flat.
 *
 * `glyph` picks the arrow: a lucide icon beside a big number, or the ▲/▼ characters a table cell
 * has room for. Either way the arrow is hidden from screen readers and the direction is spoken
 * as a word instead — "Up 12%" — because an unlabelled triangle is read as nothing at all.
 *
 * `null` means there was nothing to compare with (last month had no sales) and renders as a dash:
 * a rise from zero is not a percentage anybody can act on.
 */
export function ChangeFigure({
  change,
  glyph = 'icon',
  className,
}: {
  change: number | null;
  glyph?: 'icon' | 'triangle';
  className?: string;
}): ReactElement {
  const { t } = useTranslation();

  if (change === null) {
    return (
      <span className={cn('text-muted-foreground', className)}>
        <span aria-hidden="true">—</span>
        <span className="sr-only">{t('dashboard.analytics.noComparison')}</span>
      </span>
    );
  }

  const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
  const word =
    direction === 'up'
      ? t('dashboard.analytics.changeUp')
      : direction === 'down'
        ? t('dashboard.analytics.changeDown')
        : t('dashboard.analytics.changeNone');
  const tone =
    direction === 'up'
      ? TONE_CLASSES.green.text
      : direction === 'down'
        ? TONE_CLASSES.red.text
        : 'text-muted-foreground';

  let arrow: ReactElement | string;
  if (glyph === 'triangle') {
    arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '–';
  } else {
    const Icon = direction === 'up' ? ArrowUp : direction === 'down' ? ArrowDown : Minus;
    arrow = <Icon className="size-3.5" />;
  }

  return (
    <span
      data-direction={direction}
      className={cn('inline-flex items-center gap-0.5 font-medium tabular-nums', tone, className)}
    >
      <span aria-hidden="true" className="inline-flex">
        {arrow}
      </span>
      <span className="sr-only">{word} </span>
      {formatPercent(Math.abs(change))}
    </span>
  );
}
